import _ from "lodash";
import { BehaviorParameter } from "../../../../../types/behaviors";
import { Echelon } from "../../../../../types/force-packages";
import { InputData } from "../../../../../types/systems";

export const getBehaviorTemplate = (inputData: InputData, behaviorName: string | undefined, behaviorType: string) => {
	return inputData.behaviors?.find((behavior) => behavior.name === behaviorName && behavior.type === behaviorType)?.template;
};

// Action Queue functions

export const getQueueEntry = (queueSetting: BehaviorParameter | undefined) => {
	return findENTRYProperty(queueSetting?.settings);
};

export const getNextQueueEntry = (entry: BehaviorParameter | undefined) => {
	return findENTRYProperty(findNEXTProperty(entry?.settings)?.settings);
};

export const getQueuePlanningAction = (queueSetting: BehaviorParameter | undefined) => {
	return getPlanningAction(getQueueEntry(queueSetting)?.settings);
};

export const getQueueName = (queueSetting: BehaviorParameter | undefined) => {
	return getBehaviorName(getPlanningAction(getQueueEntry(queueSetting)?.settings));
};

export const getPreviousQueuePropertyFromPath = (agentBehaviorSettings: BehaviorParameter[], path: (string | number)[]) => {
	return getPlanningAction(_.get(agentBehaviorSettings, path.slice(0, -7)))?.settings?.[1]; // Assuming that index no. 1 is always the main input (0 is for name)
};

export const getCurrentQueueProperty = (entry: BehaviorParameter | undefined) => {
	return findPLANNING_ACTIONProperty(entry?.settings)?.settings?.[1];
};

// Behavior functions

export const getPlanningAction = (settings: BehaviorParameter[] | undefined) => {
	return settings?.find((setting) => setting.property === "PLANNING_ACTION");
};

export const getMovingBehavior = (settings: BehaviorParameter[] | undefined) => {
	return settings?.find((setting) => setting.property === "MOVING_BEHAVIOR");
};

export const getEchelonPlanningAction = (settings: BehaviorParameter[] | undefined) => {
	return settings?.find((setting) => setting.property === "ECHELON_PLANNING_ACTION");
};

export const getBehaviorName = (behavior: BehaviorParameter | undefined): string => {
	return findNAMEProperty(behavior?.settings)?.value as string;
};

export const getDependsOnProperty = (behavior: BehaviorParameter | undefined, dependsOnProperty: string | undefined) => {
	return behavior?.settings?.find((setting) => setting.property === dependsOnProperty);
};

const getDirectAgents = (echelon: Echelon) =>
	(echelon.systems || []).filter((system) => Number.isInteger(system.agent_id));

export const getEchelon = (echelons: Echelon[], echelonId: number): Echelon | undefined => {
	for (const echelon of echelons) {
		if (echelon.id === echelonId) return echelon;
		const nested = getEchelon(echelon.echelons || [], echelonId);
		if (nested) return nested;
	}
	return undefined;
};

export const getEchelonCommander = (echelon: Echelon) => getDirectAgents(echelon)[0];

const getRawConnectedFollowPathLeaderCandidates = (echelon: Echelon, baseId: number) => {
	if (getEchelonCommander(echelon)?.agent_id !== baseId) return [];

	const candidates = getDirectAgents(echelon).filter((agent) => agent.agent_id !== baseId);
	for (const child of echelon.echelons || []) {
		const commander = getEchelonCommander(child);
		if (
			commander &&
			commander.agent_id !== baseId &&
			!candidates.some((agent) => agent.agent_id === commander.agent_id)
		) {
			candidates.push(commander);
		}
	}
	return candidates;
};

export const getConnectedFollowPathRelayIds = (
	echelon: Echelon,
	baseId: number,
	leaderId: number
) =>
	getDirectAgents(echelon)
		.map((agent) => agent.agent_id)
		.filter((agentId) => agentId !== baseId && agentId !== leaderId);

export const getConnectedFollowPathChainIds = (
	echelon: Echelon,
	baseId: number,
	leaderId: number
) => {
	const leaderIsValid = getRawConnectedFollowPathLeaderCandidates(echelon, baseId).some(
		(agent) => agent.agent_id === leaderId
	);
	if (!leaderIsValid) return [];

	const chain = [leaderId, ...getConnectedFollowPathRelayIds(echelon, baseId, leaderId)];
	return chain.length >= 2 ? chain : [];
};

export const getConnectedFollowPathLeaderCandidates = (echelon: Echelon, baseId: number) =>
	getRawConnectedFollowPathLeaderCandidates(echelon, baseId).filter(
		(agent) => getConnectedFollowPathChainIds(echelon, baseId, agent.agent_id).length >= 2
	);

export const getConnectedFollowPathBaseCandidates = (echelon: Echelon) => {
	const commander = getEchelonCommander(echelon);
	if (!commander) return [];
	return getConnectedFollowPathLeaderCandidates(echelon, commander.agent_id).length
		? [commander]
		: [];
};

// Helper functions

const findNAMEProperty = (settings: BehaviorParameter[] | undefined) => {
	return settings?.find((setting) => setting.property === "NAME");
};

export const findNEXTProperty = (settings: BehaviorParameter[] | undefined) => {
	return settings?.find((setting) => setting.property === "NEXT");
};

const findENTRYProperty = (settings: BehaviorParameter[] | undefined) => {
	return settings?.find((setting) => setting.property === "ENTRY");
};

const findPLANNING_ACTIONProperty = (settings: BehaviorParameter[] | undefined) => {
	return settings?.find((setting) => setting.property === "PLANNING_ACTION");
};
